/**
 * File: StopTimerDialog.tsx
 * Path: apps/admin/src/shared/components/StopTimerDialog.tsx
 * Description: Stopping the timer: how long it ran (rounded-none up to a whole
 * minute, changeable) and what it was for, saved as a time entry.
 */

import React, { useId } from "react";
import { TIME_KINDS, TIME_KIND_LABELS, formatMinutes } from "@idk/config";
import { Button } from "@idk/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { ChoiceSelect, fieldClass, labelClass } from "@/shared/components/LeadPrimitive";
import { useStopTimerDialogController } from "../hooks/useStopTimerDialogController";
import type { RunningTimer } from "../utils/timerUtils";

export const StopTimerDialog: React.FC<{
  timer: RunningTimer;
  onSaved: () => void;
  onCancel: () => void;
}> = ({ timer, onSaved, onCancel }) => {
  const { minutes, kind, note, shownMinutes, minutesInvalid, setMinutes, setKind, setNote, save, canSave, saving } =
    useStopTimerDialogController(timer, onSaved);
  const minutesId = useId();
  const noteId = useId();

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-stack-md overflow-y-auto rounded-none p-gutter-md">
        <DialogHeader className="space-y-stack-xs text-left">
          <DialogTitle className="pr-gutter-lg text-base">Stop the timer for {timer.record.name}</DialogTitle>
          <DialogDescription className="text-xs">
            It ran for {formatMinutes(shownMinutes)}. Say what it was for.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-stack-md">
          <div className="space-y-stack-xs">
            <label htmlFor={minutesId} className={labelClass}>
              Minutes
            </label>
            <input
              id={minutesId}
              inputMode="numeric"
              className={fieldClass}
              value={minutes}
              aria-invalid={minutesInvalid}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>
          <ChoiceSelect
            label="What was it for?"
            value={kind}
            placeholder="Pick one…"
            onChange={setKind}
            options={TIME_KINDS.map((option) => ({ value: option, label: TIME_KIND_LABELS[option] }))}
          />
          <div className="space-y-stack-xs">
            <label htmlFor={noteId} className={labelClass}>
              Note (optional)
            </label>
            <input id={noteId} className={fieldClass} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-stack-xs">
            <Button type="submit" className="h-11 rounded-none" disabled={!canSave}>
              {saving ? "Saving…" : "Save the time"}
            </Button>
            <Button type="button" variant="ghost" className="h-11 rounded-none" onClick={onCancel}>
              Keep it running
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
